import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useEduAuth } from '../../context/EduAuthContext';
import {
  BookOpen, Play, CheckCircle, Lock, LogOut, ChevronRight, Clock, Zap,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/edu`;

export default function EduDashboard() {
  const navigate = useNavigate();
  const { eduUser, eduLogout } = useEduAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  const authHeader = { Authorization: `Bearer ${eduUser?.token}` };

  useEffect(() => {
    axios
      .get(`${API}/courses`, { headers: authHeader })
      .then(({ data }) => setCourses(data.data || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleEnrollAndGo = async (course) => {
    try {
      if (!course.enrolled) {
        await axios.post(`${API}/enroll/${course.id}`, {}, { headers: authHeader });
      }
      navigate(`/edu/course/${course.slug}`);
    } catch (e) {
      navigate(`/edu/course/${course.slug}`);
    }
  };

  const progress = (course) => {
    if (!course.totalLessons) return 0;
    return Math.round((course.completedLessons / course.totalLessons) * 100);
  };

  return (
    <div className="min-h-screen bg-[#0d0d10] text-white">
      {/* Header */}
      <div className="border-b border-white/5 bg-[#111115]">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-pink-500/20 to-purple-600/20 rounded-lg border border-purple-500/20">
              <BookOpen className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <span className="text-xs font-semibold text-purple-300 uppercase tracking-wider">edu.ruonn.com</span>
              <h1 className="text-base font-semibold text-white leading-tight">My Courses</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-400 hidden sm:block">{eduUser?.email}</span>
            <button
              data-testid="edu-logout-btn"
              onClick={eduLogout}
              className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-white transition-colors px-3 py-1.5 rounded-lg hover:bg-white/5"
            >
              <LogOut className="w-3.5 h-3.5" />
              Sign out
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-10">
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-white mb-2">
            Welcome back{eduUser?.email ? `, ${eduUser.email.split('@')[0]}` : ''}
          </h2>
          <p className="text-gray-400">Your learning journey continues here.</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-6 h-6 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : courses.length === 0 ? (
          <div className="text-center py-20 bg-white/3 border border-white/5 rounded-2xl">
            <BookOpen className="w-12 h-12 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400">No courses available yet</p>
          </div>
        ) : (
          <div className="grid gap-5 sm:grid-cols-2" data-testid="edu-courses-grid">
            {courses.map(course => (
              <div
                key={course.id}
                data-testid={`course-card-${course.id}`}
                className="bg-[#111115] border border-white/8 rounded-2xl overflow-hidden hover:border-purple-500/30 transition-all duration-300 group"
              >
                {/* Course header */}
                <div className="h-28 bg-gradient-to-br from-purple-900/40 to-pink-900/40 relative flex items-center justify-center">
                  <BookOpen className="w-12 h-12 text-purple-400/40" />
                  {course.isFree && (
                    <div className="absolute top-3 right-3 flex items-center gap-1 bg-green-500/20 text-green-400 text-xs font-semibold px-2 py-1 rounded-full border border-green-500/20">
                      <Zap className="w-3 h-3" /> Free
                    </div>
                  )}
                </div>

                <div className="p-5">
                  <h3 className="text-sm font-semibold text-white mb-2 line-clamp-2 leading-snug">
                    {course.title}
                  </h3>
                  <p className="text-xs text-gray-500 mb-4 line-clamp-2">{course.description}</p>

                  {/* Meta */}
                  <div className="flex items-center gap-3 text-xs text-gray-500 mb-4">
                    <span className="flex items-center gap-1">
                      <BookOpen className="w-3 h-3" />
                      {course.totalLessons} lessons
                    </span>
                    {course.enrolled && course.completedLessons > 0 && (
                      <span className="flex items-center gap-1 text-green-400">
                        <CheckCircle className="w-3 h-3" />
                        {course.completedLessons}/{course.totalLessons} done
                      </span>
                    )}
                  </div>

                  {/* Progress bar */}
                  {course.enrolled && course.totalLessons > 0 && (
                    <div className="mb-4">
                      <div className="flex justify-between text-xs text-gray-500 mb-1">
                        <span>Progress</span>
                        <span>{progress(course)}%</span>
                      </div>
                      <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-pink-500 to-purple-500 rounded-full transition-all duration-500"
                          style={{ width: `${progress(course)}%` }}
                        />
                      </div>
                    </div>
                  )}

                  <button
                    data-testid={`course-btn-${course.id}`}
                    onClick={() => handleEnrollAndGo(course)}
                    className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-medium bg-gradient-to-r from-pink-600/80 to-purple-600/80 text-white rounded-xl hover:from-pink-600 hover:to-purple-600 transition-all group-hover:shadow-lg"
                  >
                    {course.enrolled && course.completedLessons > 0 ? (
                      <>Continue <ChevronRight className="w-4 h-4" /></>
                    ) : course.enrolled ? (
                      <>Start Learning <Play className="w-4 h-4" /></>
                    ) : (
                      <>Enroll Free <Zap className="w-4 h-4" /></>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
